// Resolves the customer identity shown on reviews, confirmations and PDFs so
// every surface uses the same values.

export function isIndividualType(entityType) {
  const name = String(entityType?.type_name || entityType?.name || "").toLowerCase();
  return name.includes("individual") || name.includes("person");
}

function sameId(a, b) {
  return a != null && b != null && String(a) !== "" && String(a) === String(b);
}

export function lookupEntityType(dropdowns, id) {
  return (dropdowns?.entityTypes || []).find((row) => sameId(row.entity_type_id, id)) || null;
}

export function lookupCompany(dropdowns, id) {
  return (dropdowns?.companies || dropdowns?.company || []).find((row) => sameId(row.company_id ?? row.id, id)) || null;
}

export function lookupStatus(dropdowns, id) {
  const rows = dropdowns?.entityStatusOptions || dropdowns?.statusOptions || dropdowns?.statusTypes || [];
  return rows.find((row) => sameId(row.status_type_id, id)) || null;
}

export function statusLabel(row) {
  return row?.StatusType?.status_name || row?.status_name || row?.name || "";
}

// New-customer defaults: Active status and the company named Rivio, matched by
// name. Missing lookups produce a blocking error instead of a silent choice.
export function newCustomerDefaults(dropdowns) {
  const statuses = dropdowns?.entityStatusOptions || dropdowns?.statusOptions || dropdowns?.statusTypes || [];
  const companies = dropdowns?.companies || dropdowns?.company || [];
  const active = statuses.filter((row) => statusLabel(row).trim().toLowerCase() === "active");
  const rivio = companies.filter((row) => String(row.name || "").trim().toLowerCase() === "rivio");
  const errors = [];
  if (active.length !== 1) errors.push("No single Active status was returned. Choose a status explicitly.");
  if (rivio.length !== 1) errors.push("The Rivio company was not found. Choose a company explicitly.");
  return {
    status_type_id: active.length === 1 ? active[0].status_type_id : "",
    company_id: rivio.length === 1 ? rivio[0].company_id ?? rivio[0].id : "",
    errors,
  };
}

export function applyNewCustomerDefaults(entity, dropdowns) {
  const defaults = newCustomerDefaults(dropdowns);
  return {
    ...entity,
    status_type_id: entity.status_type_id || defaults.status_type_id,
    company_id: entity.company_id || defaults.company_id,
  };
}

export function entityDisplayName(entity) {
  const row = entity?.attributes || entity || {};
  const person = `${row.first_name || ""} ${row.last_name || ""}`.trim();
  const name = String(row.name || "").trim();
  const id = row.entity_id ?? row.id;
  return name || person || (id != null ? `Customer #${id}` : "");
}

const MISSING = "Required - missing";

/**
 * Identity for a draft (new) customer from the onboarding wizard state.
 * Lookup labels that cannot be resolved are reported, never blanked.
 */
export function draftCustomerIdentity(entity, dropdowns, { lookupsLoaded = true } = {}) {
  const entityType = lookupEntityType(dropdowns, entity?.entity_type_id);
  const company = lookupCompany(dropdowns, entity?.company_id);
  const status = lookupStatus(dropdowns, entity?.status_type_id);
  const individual = isIndividualType(entityType);
  const personName = `${entity?.first_name || ""} ${entity?.last_name || ""}`.trim();
  const name = individual ? personName : String(entity?.name || "").trim();
  const missing = [];
  const unresolved = [];
  if (!name) missing.push("Customer name");
  if (!entity?.entity_type_id) missing.push("Entity type");
  else if (!entityType) unresolved.push("Entity type");
  if (!entity?.company_id) missing.push("Company");
  else if (!company) unresolved.push("Company");
  if (!entity?.status_type_id) missing.push("Status");
  else if (!status) unresolved.push("Status");
  const unresolvedLabel = (id) => (lookupsLoaded ? `ID ${id} (label unavailable)` : `Loading… (ID ${id})`);
  return {
    name: name || MISSING,
    entityType: entityType?.type_name || (entity?.entity_type_id ? unresolvedLabel(entity.entity_type_id) : MISSING),
    company: company?.name || (entity?.company_id ? unresolvedLabel(entity.company_id) : MISSING),
    status: statusLabel(status) || (entity?.status_type_id ? unresolvedLabel(entity.status_type_id) : MISSING),
    missing,
    unresolved,
    complete: missing.length === 0,
  };
}

export const REQUIRED_MISSING = MISSING;
